import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Download,
  Calendar,
  Users,
  DollarSign,
  Activity,
  Brain
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const ltvData = [
  { month: 'Jan', beforeAI: 2400, afterAI: 2800 },
  { month: 'Feb', beforeAI: 2500, afterAI: 3100 },
  { month: 'Mar', beforeAI: 2600, afterAI: 3400 },
  { month: 'Apr', beforeAI: 2550, afterAI: 3600 },
  { month: 'May', beforeAI: 2700, afterAI: 3900 },
  { month: 'Jun', beforeAI: 2650, afterAI: 4200 },
];

const dropoutData = [
  { month: 'Jan', rate: 18 },
  { month: 'Feb', rate: 16 },
  { month: 'Mar', rate: 14 },
  { month: 'Apr', rate: 12 },
  { month: 'May', rate: 10 },
  { month: 'Jun', rate: 8.5 },
];

const outcomesData = [
  { month: 'Jan', score: 72 },
  { month: 'Feb', score: 74 },
  { month: 'Mar', score: 76 },
  { month: 'Apr', score: 79 },
  { month: 'May', score: 81 },
  { month: 'Jun', score: 84 },
];

const utilizationData = [
  { month: 'Jan', utilization: 78 },
  { month: 'Feb', utilization: 80 },
  { month: 'Mar', utilization: 82 },
  { month: 'Apr', utilization: 85 },
  { month: 'May', utilization: 87 },
  { month: 'Jun', utilization: 89 },
];

export default function AdminReports() {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Reports & Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">AI impact metrics and investor-ready insights</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Calendar className="w-4 h-4 mr-2" />
            Last 6 Months
          </Button>
          <Button>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'LTV Improvement', value: '+58%', subtitle: 'Since AI implementation', icon: DollarSign, trend: 'up', color: 'text-success' },
          { label: 'Dropout Reduction', value: '-52%', subtitle: 'From 18% to 8.5%', icon: Users, trend: 'down', color: 'text-success' },
          { label: 'Outcomes Improvement', value: '+17%', subtitle: 'Average score increase', icon: Activity, trend: 'up', color: 'text-success' },
          { label: 'Therapist Utilization', value: '+14%', subtitle: 'Efficiency gain', icon: Brain, trend: 'up', color: 'text-success' },
        ].map((metric, idx) => (
          <Card key={idx}>
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{metric.label}</p>
                  <p className={`text-2xl font-display font-bold mt-1 ${metric.color}`}>{metric.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{metric.subtitle}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-success-light flex items-center justify-center">
                  {metric.trend === 'up' ? (
                    <TrendingUp className="w-5 h-5 text-success" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-success" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LTV Improvement */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Customer LTV: Before vs After AI</CardTitle>
            <CardDescription>Average lifetime value per patient (RM)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ltvData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${v/1000}k`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`RM ${value.toLocaleString()}`, '']}
                  />
                  <Legend />
                  <Bar dataKey="beforeAI" name="Before AI" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="afterAI" name="After AI" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Dropout Reduction */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dropout Rate Trend</CardTitle>
            <CardDescription>Monthly patient dropout percentage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dropoutData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" domain={[0, 25]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`${value}%`, 'Dropout Rate']}
                  />
                  <Line type="monotone" dataKey="rate" stroke="hsl(var(--success))" strokeWidth={3} dot={{ fill: 'hsl(var(--success))' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Outcomes Improvement */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Patient Outcomes Score</CardTitle>
            <CardDescription>Average improvement score (0-100)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={outcomesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" domain={[60, 100]} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--info))" strokeWidth={3} dot={{ fill: 'hsl(var(--info))' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Therapist Utilization */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Therapist Utilization Rate</CardTitle>
            <CardDescription>Percentage of available slots filled</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={utilizationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" domain={[70, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`${value}%`, 'Utilization']}
                  />
                  <Line type="monotone" dataKey="utilization" stroke="hsl(var(--chart-4))" strokeWidth={3} dot={{ fill: 'hsl(var(--chart-4))' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* ROI Summary */}
      <Card>
        <CardHeader>
          <CardTitle>AI ROI Summary</CardTitle>
          <CardDescription>Key return on investment metrics since AI implementation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-success-light rounded-xl">
              <p className="text-sm text-success font-medium">Revenue Impact</p>
              <p className="text-3xl font-display font-bold text-success mt-2">+RM 2.4M</p>
              <p className="text-sm text-muted-foreground mt-1">Annualized additional revenue from improved retention</p>
            </div>
            <div className="p-4 bg-info-light rounded-xl">
              <p className="text-sm text-info font-medium">Cost Savings</p>
              <p className="text-3xl font-display font-bold text-info mt-2">RM 580K</p>
              <p className="text-sm text-muted-foreground mt-1">Operational efficiency gains and reduced manual work</p>
            </div>
            <div className="p-4 bg-primary-light rounded-xl">
              <p className="text-sm text-primary font-medium">Payback Period</p>
              <p className="text-3xl font-display font-bold text-primary mt-2">4.2 months</p>
              <p className="text-sm text-muted-foreground mt-1">Time to recover AI implementation investment</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
